import { faFileLines, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useState } from 'react';

import MainButton from '../../../components/agv-button';
import InputText from '../../../components/agv-forms-input';
import {
  Banner,
  Card,
  FileDropZone,
  formatFileSize,
  SectionLabel,
} from '../clients-ui';
import {
  DOCUMENT_TEMPLATE_TYPES,
  TEMPLATE_ACCEPTED_EXTENSIONS,
  TEMPLATE_MAX_SIZE_MB,
} from '../client-defaults';

const TYPE_KEYS = Object.keys(DOCUMENT_TEMPLATE_TYPES).filter(
  (key) => key !== 'properties'
);

const stripExtension = (fileName) => fileName.replace(/\.[^.]+$/, '');

export default function DocumentsStep({
  templates,
  onUpload,
  onArchive,
  isUploading,
  archivingId,
}) {
  const [type, setType] = useState(DOCUMENT_TEMPLATE_TYPES.DELIVERY_NOTE);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);

  const handleFile = (selectedFile) => {
    setFile(selectedFile);
    if (!name.trim()) setName(stripExtension(selectedFile.name));
  };

  const handleUpload = async () => {
    const isUploaded = await onUpload({ type, name: name.trim(), file });

    if (isUploaded) {
      setFile(null);
      setName('');
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card
        title="Templates de documents (WIP)"
        subtitle="Modèles des documents générés pour ce client (bon de livraison, lettre de voiture…). Étape facultative : un template peut être déposé plus tard."
      >
        <Banner tone="warn">
          Fonctionnalité en cours de développement : les templates déposés sont
          stockés et rattachés au client, mais la génération des documents (bons
          de livraison, lettres de voiture…) utilise encore les modèles intégrés
          à l’API. Un template déposé ici n’a pour l’instant aucun effet sur les
          documents du client.
        </Banner>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <SectionLabel>Type de document</SectionLabel>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {TYPE_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setType(key)}
                className={`tw-rounded-[5px] tw-border tw-border-solid tw-px-3 tw-py-1.5 tw-text-[13px] ${
                  type === key
                    ? 'tw-border-main tw-bg-[rgba(7,153,146,0.06)] tw-font-medium tw-text-main-active'
                    : 'tw-border-[#dfe3e6] tw-bg-white tw-text-black hover:tw-border-[#d0d4da]'
                }`}
              >
                {DOCUMENT_TEMPLATE_TYPES.properties[key].display_name}
              </button>
            ))}
          </div>
        </div>

        <FileDropZone
          accept={TEMPLATE_ACCEPTED_EXTENSIONS}
          maxSizeMb={TEMPLATE_MAX_SIZE_MB}
          onFile={handleFile}
          disabled={isUploading}
        >
          {file ? (
            <span className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-text-black">
              <FontAwesomeIcon
                icon={faFileLines}
                className="tw-size-4 tw-text-main"
              />
              {file.name} · {formatFileSize(file.size)}
              <span className="tw-text-[11px] tw-text-[#76808c]">
                (cliquer pour changer)
              </span>
            </span>
          ) : null}
        </FileDropZone>

        {file && (
          <div className="tw-flex tw-items-end tw-gap-3 mq750:tw-flex-col mq750:tw-items-stretch">
            <div className="tw-flex-1">
              <InputText
                value={name}
                setValue={setName}
                fieldTitle="Nom du template"
                isRequired
                emptyError={'Le champ "Nom du template" ne peut être vide.'}
              />
            </div>
            <MainButton
              text="Déposer le template"
              onClick={handleUpload}
              disabled={!name.trim()}
              isLoading={isUploading}
            />
          </div>
        )}
      </Card>

      <Card
        title="Templates déposés"
        subtitle={`${templates.length} template${templates.length > 1 ? 's' : ''} actif${templates.length > 1 ? 's' : ''}`}
      >
        {templates.length === 0 ? (
          <p className="tw-m-0 tw-text-[13px] tw-text-[#76808c]">
            Aucun template pour le moment.
          </p>
        ) : (
          <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
            {templates.map((template) => (
              <div
                key={template.id}
                className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-px-3 tw-py-2 last:tw-border-b-0"
              >
                <FontAwesomeIcon
                  icon={faFileLines}
                  className="tw-size-4 tw-text-[#9aa0a6]"
                />
                <div className="tw-flex tw-flex-1 tw-flex-col">
                  <span className="tw-text-[13px] tw-font-medium tw-text-black">
                    {template.name}
                  </span>
                  <span className="tw-text-[11px] tw-text-[#76808c]">
                    {DOCUMENT_TEMPLATE_TYPES.properties[template.type]
                      ?.display_name || template.type}
                    {' · '}
                    {template.file_name} · {formatFileSize(template.size)}
                    {template.created_at &&
                      ` · ${moment(template.created_at).format('DD/MM/YYYY HH:mm')}`}
                  </span>
                </div>
                <MainButton
                  variant="secondary"
                  icon={faTrash}
                  onClick={() => onArchive(template.id)}
                  isLoading={archivingId === template.id}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
